/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from "../../../../../../@BASE/utils/BASEUtils";
// material-ui components
import { styled } from "@mui/material/styles";
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";
import TutorialVideoSegmentArray from '../shared/TutorialVideoSegmentArray';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const TutorialVideo = (props) => {
  const { control, formState, setValue } = props.methods;
  const { errors } = formState;
  const inputDir = props.inputDir;

  const [fileError, setFileError] = useState(null);

  const recreateSeconds = (second) => {
    if (second.includes(",")) {
      const split = second.split(",");
      return parseFloat(split[0] + "." + split[1]);
    }

    return second;
  };

  const separateTimeStamp = (time, timePeriod) => {
    if (typeof time !== "string" || !time.includes(":")) {
      console.warn("Invalid time format. Expected HH:MM:SS format.");
      return null;
    }
    const timeArray = time.split(":");

    switch (timePeriod) {
      case "hour":
        return parseInt(timeArray[0]);
      case "minute":
        return parseInt(timeArray[1]);
      case "second":
        return recreateSeconds(timeArray[2]);
      default:
        return null;
    }
  };

  const validateFile = (file) => {
    if (file.type !== "application/json" && !file.name.includes(".srt")) {
      setFileError("Invalid file type. Please upload a JSON or SRT file.");
      return false;
    }
    return true;
  };

  const readSRT = (data) => {
    const blocksArray = data.split("\r\n\r\n");
    // clean up the array by removing empty strings
    const filteredBlocks = blocksArray.filter((block) => block.trim() !== "");
    const ccArray = filteredBlocks.map((block, i) => {
      try {
        const blockArray = block.split("\r\n");
        // if the blockArray has less than 2 items, it's not a valid block
        if (blockArray.length < 2) {
          return null;
        }

        // const index = blockArray[0];
        const timestamps = blockArray[1].split(" --> ");
        // if the timestamps array has more than 2 items, it's not a valid block
        if (timestamps.length !== 2) {
          return null;
        }
        const id = BASEUtils.generateGUID();
        const review = {
            development: {
              status: "development",
              approved: false,
              approval_date: "",
            },
            production: {
              status: "not_started",
              approved: false,
              approval_date: "",
            },
          };
        const start_minute = separateTimeStamp(timestamps[0], "minute");
        const start_second = separateTimeStamp(timestamps[0], "second");
        const end_minute = separateTimeStamp(timestamps[1], "minute");
        const end_second = separateTimeStamp(timestamps[1], "second");
        const text_en = blockArray.slice(2).join(" ");
        const description = "";
        
        return { id, review, start_minute, start_second, end_minute, end_second, text_en, description };
      } catch (err) {
        console.error("Error processing SRT block:", err);
        return null;
      }
    });
    // one last cleanup to remove any null items
    const validCCArray = ccArray.filter((item) => item !== null);
    setValue(`content.core`, validCCArray);
  };

  const readJSON = (result) => {
    const data = JSON.parse(result);
    const ccArray = data.map((ccItem) => {
      const id = BASEUtils.generateGUID();
      const review = {
            development: {
              status: "development",
              approved: false,
              approval_date: "",
            },
            production: {
              status: "not_started",
              approved: false,
              approval_date: "",
            },
          };
      const start_minute = separateTimeStamp(ccItem.start_time, "minute");
      const start_second = separateTimeStamp(ccItem.start_time, "second");
      const end_minute = separateTimeStamp(ccItem.end_time, "minute");
      const end_second = separateTimeStamp(ccItem.end_time, "second");
      const text_en = ccItem.text;
      const description = "";

      return { id, review, start_minute, start_second, end_minute, end_second, text_en, description };
    });

    setValue(`content.core`, ccArray);
  };

  const getFile = (e) => {
    const file = e.target.files[0];

    if (!validateFile(file)) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      try {
        if (file.name.includes(".srt")) {
          readSRT(reader.result);
        }
        if (file.type === "application/json") {
          readJSON(reader.result);
        }
      } catch (err) {
        setFileError("Invalid file. Please upload a real valid file.");
      }
    };
  };

  useEffect(() => {
    if (fileError) console.warn("File Error:", fileError);
  }, [fileError]);

  return (
    <div>
      <NumberedHeading number="1" title="Video Transcript" instructions="Enter a transcript for the video." required />
      <Controller
        control={control}
        name="content.transcript"
        render={({ field }) => (
          <TextField
            className="mt-16"
            {...field}
            label="Video"
            placeholder="Video Transcript"
            id="video"
            error={!!errors.content?.transcript}
            helperText={errors.content?.transcript?.message}
            variant="outlined"
            fullWidth
            multiline
            minRows={5}
            maxRows={10}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading number="2" title="Video Captions" instructions="Using the controls below, create the necessary video captions." required />
      <Button
        component="label"
        role={undefined}
        variant="contained"
        className="mt-16 mb-24"
        color="secondary"
        tabIndex={0}
        startIcon={<BASESvgIcon>heroicons-solid:upload</BASESvgIcon>}
      >
        Upload
        <VisuallyHiddenInput
          type="file"
          onChange={(e) => getFile(e)}
          accept=".json, .srt"
        />
      </Button>
      <Controller
        control={control}
        name="content.core"
        render={({field}) => <TutorialVideoSegmentArray inputDir={inputDir} {...{control, errors}} />}
      />
    </div>
  );
};

export default TutorialVideo;